import * as React from "react";
import {useEffect, useState} from "react";
import {InFormReportingApi} from "../../api/InFormReportingApi";
import {useParams} from "react-router";
import {IInformSummary} from "../../model/inform/InformSummary";
import {IInformPerformance} from "../../model/inform/InformPerformance";
import {IInformStaff} from "../../model/inform/InformStaff";
import {IInformBusiness} from "../../model/inform/InformBusiness";
import {IInformProcess} from "../../model/inform/InformProcess";
import {IInformRiskManagement} from "../../model/inform/InformRiskManagement";
import {IInformTermsAndConditions} from "../../model/inform/InformTermsAndConditions";
import {IInformESG} from "../../model/inform/InformESG";
import {ErrorComponent} from "../base/Error.component";
import {Map} from "immutable";
import {ProductSummary} from "../../model/product/ProductSummary";
import {IApplicationRootState} from "../../../mainReducerMap";
import {isEditingPlanPortfolio, portfolioProductSummariesSelector} from "../../../mainReducerMapSelectors";
import {getSuccessData} from "../common/commonStates";
import {connect} from "react-redux";
import {injectIntl} from "react-intl";
import {multipleApiRequesterWrapper} from "../common/MultipleApiRequesterWrapper";
import {allProductsRequestPair, shareClassRequestPair} from "../common/RequesterPairs";
import {InformSubComponent} from "./InformSubComponent";
import {renderData} from "./InformUtils";

interface IProductId {
    productId: string
}

export interface IInFormComponentsPageProps {
    portfolioProductSummaries: Map<number, ProductSummary> | null;
    editingPlanPortfolio: boolean;
}

export const InFormComponentsPage: React.FunctionComponent<IInFormComponentsPageProps> = (props) => {
    const [informReports, setInformReports] = useState<IInformSummary | undefined>(undefined);
    const [informPerformanceReports, setInformPerformanceReports] = useState<IInformPerformance | undefined>(undefined);
    const [informStaffReports, setInformStaffReports] = useState<IInformStaff | undefined>(undefined);
    const [informBusinessReports, setInformBusinessReports] = useState<IInformBusiness | undefined>(undefined);
    const [informRiskManagementReports, setInformRiskManagementReports] =
        useState<IInformRiskManagement | undefined>(undefined);
    const [informProcessReports, setInformProcessReports] = useState<IInformProcess | undefined>(undefined);
    const [informTermsAndConditionsReports, setInformTermsAndConditionsReports] =
        useState<IInformTermsAndConditions | undefined>(undefined);
    const [informESG, setInformESG] = useState<IInformESG | undefined>(undefined);
    const [renderErrorComponent, setRenderErrorComponent] = useState<boolean>(false);
    const {productId} = useParams<IProductId>();

    const isProductAccessible = props.portfolioProductSummaries?.has(+productId)
        || props.editingPlanPortfolio;

    const getInformSummary = () => {
        InFormReportingApi.fetchInformSummary(+productId).then((response) => {
            setInformReports(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    const getInformPerformance = () => {
        InFormReportingApi.fetchInformPerformance(+productId).then((response) => {
            setInformPerformanceReports(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    const getInformStaff = () => {
        InFormReportingApi.fetchInformStaff(+productId).then((response) => {
            setInformStaffReports(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    const getInformBusiness = () => {
        InFormReportingApi.fetchInformBusiness(+productId).then((response) => {
            setInformBusinessReports(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    const getInformRiskManagement = () => {
        InFormReportingApi.fetchInformRiskManagement(+productId).then((response) => {
            setInformRiskManagementReports(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    const getInformProcess = () => {
        InFormReportingApi.fetchInformProcess(+productId).then((response) => {
            setInformProcessReports(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    const getInformTermsAndConditions = () => {
        InFormReportingApi.fetchInformTermsAndConditions(+productId).then((response) => {
            setInformTermsAndConditionsReports(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    const getInformESG = () => {
        InFormReportingApi.fetchInformESG(+productId).then((response) => {
            setInformESG(response);
        }).catch(() => {
            setRenderErrorComponent(true);
        });
    };

    useEffect(() => {
        if (isProductAccessible) {
            getInformSummary();
            getInformPerformance();
            getInformStaff();
            getInformBusiness();
            getInformRiskManagement();
            getInformProcess();
            getInformTermsAndConditions();
            getInformESG();
        }
    }, []);

    if (!isProductAccessible) {
        return <ErrorComponent/>;
    }

    const renderInformSubComponents = () => {
        return <div className="inform-components__container" data-testid="inform-components__container">
            <h2>In Form Report Data</h2>
            <h3>Summary Report</h3>
            {renderData(informReports)}
            <InformSubComponent name={"Business"} hasData={!!informBusinessReports}
                                summary={informBusinessReports?.summary} details={informBusinessReports?.details}/>
            <InformSubComponent name={"Investment Staff"} hasData={!!informBusinessReports}
                                summary={informStaffReports?.summary} details={informStaffReports?.details}/>
            <InformSubComponent name={"Investment Process"} hasData={!!informProcessReports}
                                summary={informProcessReports?.summary} details={informProcessReports?.details}/>
            <InformSubComponent name={"Risk Management"} hasData={!!informRiskManagementReports}
                                summary={informRiskManagementReports?.summary}
                                details={informRiskManagementReports?.details}/>
            <InformSubComponent name={"Performance"} hasData={!!informPerformanceReports}
                                summary={informPerformanceReports?.summary}
                                details={informPerformanceReports?.details}/>
            <InformSubComponent name={"Terms & Conditions"} hasData={!!informTermsAndConditionsReports}
                                summary={informTermsAndConditionsReports?.summary}
                                details={informTermsAndConditionsReports?.details}/>
            <InformSubComponent name={"ESG"} hasData={!!informESG}
                                summary={informESG?.summary} details={informESG?.details}/>
        </div>;
    };

    return renderErrorComponent ? <ErrorComponent/> : renderInformSubComponents();
};

export const mapStateToProps = (state: IApplicationRootState): IInFormComponentsPageProps => {
    return {
        editingPlanPortfolio: isEditingPlanPortfolio(state),
        portfolioProductSummaries: getSuccessData(portfolioProductSummariesSelector(state))!,
    };
};

const connectedInformComponent = connect<IInFormComponentsPageProps>(mapStateToProps)(InFormComponentsPage);
export default injectIntl(multipleApiRequesterWrapper(
    connectedInformComponent,
    [
        allProductsRequestPair(),
        shareClassRequestPair,
    ],
));